import { useCallback } from 'react';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Branch,
  CreateBranchRequest,
  UpdateBranchRequest,
  PosUser,
  CreatePosUserRequest,
  UpdatePosUserRequest,
  AdminUser,
  CreateAdminUserRequest,
  UpdateAdminUserRequest,
  ExternalTransfer,
  MtCenterTransaction,
  MtCenterTransactionsFilters,
  MtCenterTransactionsResponse,
  Customer,
  UpdateCustomerRequest,
  SendOtpRequest,
  SendOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  CustomerBalance,
  CustomerInvestment,
  CustomerPurchase,
  CustomerInfo,
  InsuranceRequest,
  InsuranceRequestsResponse,
  UpdateInsuranceStatusRequest,
  InvestmentRate,
  UpdateInvestmentRateRequest,
  ApiError,
  ApiErrorResponse
} from '../types/api';
import { getApiBaseUrl, isUsingProxy } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import {
  apiStatusCandidatesForUi,
  normalizeExternalTransferStatus,
  readTransferIdFromApi,
  readTransferStatusFromApi,
} from '../utils/externalTransferStatus';
import { ExternalTransferStatus } from '../types/api';

// Get the base URL based on environment
const BASE_URL = getApiBaseUrl();

// Debug function to log the actual URL being used
const getFullUrl = (endpoint: string) => {
  const fullUrl = `${BASE_URL}${endpoint}`;
  console.log('🌐 API Request URL:', fullUrl);
  console.log('🔧 Using proxy:', isUsingProxy());
  console.log('🏗️ Environment:', process.env.NODE_ENV);
  console.log('📍 Base URL:', BASE_URL);
  return fullUrl;
};

export const useAdminAPI = () => {
  const { accessToken, isAuthenticated, logout } = useAuth();

  // Helper function to make authenticated requests
  const makeRequest = useCallback(async <T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const url = getFullUrl(endpoint);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add authorization header if token exists
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
      console.log('🔑 Adding Authorization header with token:', accessToken.substring(0, 20) + '...');
    } else {
      console.warn('⚠️ No access token available for request');
    }

    let response: Response;
    try {
      console.log('📤 Making request:', {
        method: options.method || 'GET',
        url,
        hasToken: !!accessToken
      });

      response = await fetch(url, {
        ...options,
        headers,
        credentials: options.credentials ?? 'include',
      });

      console.log('📥 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
    } catch (networkError) {
      console.error('❌ Network error:', networkError);
      
      // Check if it's a CORS error
      if (networkError instanceof TypeError && networkError.message.includes('CORS')) {
        throw new ApiError(
          'Error de CORS: El servidor no permite peticiones desde este dominio. El servidor necesita configurar CORS para permitir requests desde localhost.',
          0,
          'CORS_ERROR'
        );
      }
      
      // Check if it's a network/fetch error
      if (networkError instanceof TypeError && networkError.message.includes('fetch')) {
        throw new ApiError(
          'No se puede conectar al servidor. Verifica que el servidor esté ejecutándose y que no haya problemas de CORS.',
          0,
          'NETWORK_ERROR'
        );
      }
      
      throw new ApiError(
        `Error de conexión: ${networkError instanceof Error ? networkError.message : 'Unknown network error'}`,
        0,
        'NETWORK_ERROR'
      );
    }

    // Handle 401 Unauthorized - only logout if we actually had a token
    // This prevents logout on refresh if token wasn't loaded yet
    if (response.status === 401) {
      console.warn('🔒 Unauthorized (401) response received');
      console.warn('🔑 Token state:', { 
        hasToken: !!accessToken, 
        tokenLength: accessToken?.length,
        tokenPreview: accessToken ? accessToken.substring(0, 20) + '...' : 'none'
      });
      
      // Only logout if we actually had a token (meaning it was invalid/expired)
      // If we didn't have a token, it's just a missing auth, not an expired one
      if (accessToken) {
        console.warn('🔒 Token was present but invalid/expired - logging out');
        logout();
      } else {
        console.warn('⚠️ No token was present for this request - not logging out (may be initial load)');
      }
      
      throw new ApiError('Unauthorized access. Please login again.', 401, 'UNAUTHORIZED');
    }

    // Parse response
    let responseData;
    try {
      const text = await response.text();
      console.log('📄 Raw response text:', text);
      responseData = text ? JSON.parse(text) : {};
      console.log('📄 Parsed response data:', responseData);
    } catch (parseError) {
      // Para DELETE, algunas APIs devuelven 204 No Content sin body
      if (response.status === 204 || response.status === 200) {
        console.log('✅ DELETE successful (204/200 No Content)');
        return {} as T;
      }
      console.error('❌ Failed to parse JSON response:', parseError);
      throw new ApiError('Invalid JSON response from server', response.status, 'PARSE_ERROR');
    }

    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = responseData as ApiErrorResponse;
      console.error('❌ API Error:', errorData);
      throw new ApiError(
        errorData.message || 'Request failed',
        errorData.statusCode || response.status,
        errorData.error || 'UNKNOWN_ERROR'
      );
    }

    // Para DELETE, verificar que el status sea 200, 204, o 202
    if (response.status === 204 || response.status === 202) {
      console.log('✅ DELETE operation accepted by server');
      return {} as T;
    }

    return responseData as T;
  }, [accessToken, logout]);

  // Brands (Marcas)
  const createBrand = useCallback(async (data: CreateBrandRequest): Promise<Brand> => {
    console.log('🏷️ Creating brand:', data);
    return makeRequest<Brand>('/admin/brands', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const getBrands = useCallback(async (): Promise<Brand[]> => {
    console.log('📋 Getting brands');
    return makeRequest<Brand[]>('/admin/brands', {
      method: 'GET',
    });
  }, [makeRequest]);

  const updateBrand = useCallback(async (id: string, data: UpdateBrandRequest): Promise<Brand> => {
    console.log('✏️ Updating brand:', id, data);
    return makeRequest<Brand>(`/admin/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const deleteBrand = useCallback(async (id: string): Promise<void> => {
    console.log('🗑️ Deleting brand:', id);
    const response = await makeRequest<any>(`/admin/brands/${id}`, {
      method: 'DELETE',
    });
    console.log('✅ Brand delete response:', response);
    return response;
  }, [makeRequest]);

  // Branches (Sucursales)
  const createBranch = useCallback(async (data: CreateBranchRequest): Promise<Branch> => {
    console.log('🏢 Creating branch:', data);
    return makeRequest<Branch>('/admin/branches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const getBranches = useCallback(async (brandId?: string): Promise<Branch[]> => {
    console.log('📋 Getting branches for brand:', brandId);
    const queryParam = brandId ? `?brandId=${encodeURIComponent(brandId)}` : '';
    return makeRequest<Branch[]>(`/admin/branches${queryParam}`, {
      method: 'GET',
    });
  }, [makeRequest]);

  const updateBranch = useCallback(async (id: string, data: UpdateBranchRequest): Promise<Branch> => {
    console.log('✏️ Updating branch:', id, data);
    return makeRequest<Branch>(`/admin/branches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const deleteBranch = useCallback(async (id: string): Promise<void> => {
    console.log('🗑️ Deleting branch:', id);
    const response = await makeRequest<any>(`/admin/branches/${id}`, {
      method: 'DELETE',
    });
    console.log('✅ Branch delete response:', response);
    return response;
  }, [makeRequest]);

  // POS Users
  const createPosUser = useCallback(async (data: CreatePosUserRequest): Promise<PosUser> => {
    console.log('👤 Creating POS user:', data);
    return makeRequest<PosUser>('/admin/pos-users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const getPosUsers = useCallback(async (branchId?: string): Promise<PosUser[]> => {
    console.log('📋 Getting POS users for branch:', branchId);
    const queryParam = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
    return makeRequest<PosUser[]>(`/admin/pos-users${queryParam}`, {
      method: 'GET',
    });
  }, [makeRequest]);

  const updatePosUser = useCallback(async (id: string, data: UpdatePosUserRequest): Promise<PosUser> => {
    console.log('✏️ Updating POS user:', id, data);
    return makeRequest<PosUser>(`/admin/pos-users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const deletePosUser = useCallback(async (id: string): Promise<void> => {
    console.log('🗑️ Deleting POS user:', id);
    const response = await makeRequest<any>(`/admin/pos-users/${id}`, {
      method: 'DELETE',
    });
    console.log('✅ POS user delete response:', response);
    return response;
  }, [makeRequest]);

  // Admin Users
  const createAdminUser = useCallback(async (data: CreateAdminUserRequest): Promise<AdminUser> => {
    console.log('👑 Creating admin user:', { ...data, password: '***' });
    return makeRequest<AdminUser>('/admin/admin-users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const getAdminUsers = useCallback(async (): Promise<AdminUser[]> => {
    console.log('📋 Getting admin users');
    return makeRequest<AdminUser[]>('/admin/admin-users', {
      method: 'GET',
    });
  }, [makeRequest]);

  const updateAdminUser = useCallback(async (
    id: string,
    data: UpdateAdminUserRequest
  ): Promise<AdminUser> => {
    console.log('✏️ Updating admin user:', id, { ...data, password: data.password ? '***' : undefined });
    return makeRequest<AdminUser>(`/admin/admin-users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const deleteAdminUser = useCallback(async (id: string): Promise<void> => {
    console.log('🗑️ Deactivating admin user:', id);
    const response = await makeRequest<any>(`/admin/admin-users/${id}`, {
      method: 'DELETE',
    });
    return response;
  }, [makeRequest]);

  // External Transfers
  const getExternalTransfers = useCallback(async (filters?: {
    status?: ExternalTransferStatus;
    userId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    search?: string;
  }): Promise<ExternalTransfer[]> => {
    console.log('💸 Getting external transfers', filters);
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (typeof filters?.limit === 'number') params.append('limit', String(filters.limit));
    if (filters?.search) params.append('search', filters.search);
    const query = params.toString();

    const rawResponse = await makeRequest<any>(`/admin/transactions/external${query ? `?${query}` : ''}`, {
      method: 'GET',
    });
    
    console.log('💸 Raw response from backend:', JSON.stringify(rawResponse, null, 2));
    
    // Handle different response structures
    let transfers: any[] = [];
    if (Array.isArray(rawResponse)) {
      transfers = rawResponse;
    } else if (rawResponse && Array.isArray(rawResponse.data)) {
      transfers = rawResponse.data;
    } else if (rawResponse && rawResponse.transfers) {
      transfers = rawResponse.transfers;
    }
    
    console.log('💸 Processed transfers array:', transfers);
    if (transfers.length > 0) {
      console.log('💸 First transfer raw data:', JSON.stringify(transfers[0], null, 2));
      console.log('💸 First transfer fields:', Object.keys(transfers[0]));
    }
    
    // Map the response to match our interface
    const mappedTransfers: ExternalTransfer[] = transfers.map((transfer: any) => {
      const mapped: ExternalTransfer = {
        id: readTransferIdFromApi(transfer),
        user_id: transfer.user_id || '',
        transaction_id: transfer.transaction_id || null,
        beneficiary_name: transfer.beneficiary_name || '',
        bank_name: transfer.bank_name || '',
        clabe: transfer.clabe || '',
        amount: transfer.amount ?? 0,
        description: transfer.description || null,
        reference: transfer.reference || null,
        rawStatus: readTransferStatusFromApi(transfer),
        status: normalizeExternalTransferStatus(readTransferStatusFromApi(transfer)),
        fee_amount: transfer.fee_amount ?? 0,
        tax_amount: transfer.tax_amount ?? 0,
        metadata: transfer.metadata || {},
        created_at: transfer.created_at || '',
        updated_at: transfer.updated_at || '',
        approved_by: transfer.approved_by ?? null,
        approved_at: transfer.approved_at ?? null,
        user: transfer.user
          ? {
              id: transfer.user.id,
              first_name: transfer.user.first_name,
              last_name: transfer.user.last_name,
              phone_number: transfer.user.phone_number,
              email: transfer.user.email,
            }
          : undefined,
        // Datos del emisor
        emitter_name: transfer.emitter_name,
        emitter_phone: transfer.emitter_phone,
        emitter_email: transfer.emitter_email,
      };
      
      console.log('💸 Mapped transfer:', {
        id: mapped.id,
        reference: mapped.reference,
        beneficiary_name: mapped.beneficiary_name,
        bank_name: mapped.bank_name,
        clabe: mapped.clabe,
        amount: mapped.amount,
        emitter_name: mapped.emitter_name,
        emitter_phone: mapped.emitter_phone,
      });
      
      return mapped;
    });
    
    return mappedTransfers;
  }, [makeRequest]);

  const updateExternalTransferStatus = useCallback(
    async (transferId: string, uiStatus: ExternalTransferStatus): Promise<ExternalTransfer> => {
      const endpoint = `/admin/transactions/external/${transferId}/status`;
      const candidates = apiStatusCandidatesForUi(uiStatus);
      let lastError: unknown;

      for (const status of candidates) {
        try {
          console.log('✏️ Updating external transfer status:', transferId, { status });
          const response = await makeRequest<any>(endpoint, {
            method: 'PUT',
            body: JSON.stringify({ status }),
          });
          const rawStatus = readTransferStatusFromApi(response ?? {});
          return {
            ...response,
            rawStatus,
            status: normalizeExternalTransferStatus(rawStatus),
          } as ExternalTransfer;
        } catch (err) {
          lastError = err;
          const message = err instanceof ApiError ? err.message.toLowerCase() : '';
          const canRetryWithNext =
            uiStatus === 'completed' &&
            candidates.length > 1 &&
            (message.includes('approved') || message.includes('solo se permite'));
          if (!canRetryWithNext) {
            throw err;
          }
        }
      }

      throw lastError instanceof Error ? lastError : new Error('No se pudo actualizar el estado.');
    },
    [makeRequest]
  );

  const completeExternalTransfer = useCallback(
    async (transferId: string): Promise<ExternalTransfer> =>
      updateExternalTransferStatus(transferId, 'completed'),
    [updateExternalTransferStatus]
  );

  // MT Center transactions (servicios + TAE)
  const getMtCenterTransactions = useCallback(
    async (filters?: MtCenterTransactionsFilters): Promise<MtCenterTransactionsResponse> => {
      console.log('📡 Getting MT Center transactions', filters);
      const params = new URLSearchParams();
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.phone) params.append('phone', filters.phone);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.kind && filters.kind !== 'all') params.append('kind', filters.kind);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.search) params.append('search', filters.search);
      if (typeof filters?.limit === 'number') params.append('limit', String(filters.limit));
      if (typeof filters?.offset === 'number') params.append('offset', String(filters.offset));
      const query = params.toString();

      const rawResponse = await makeRequest<any>(
        `/admin/transactions/mt-center${query ? `?${query}` : ''}`,
        { method: 'GET' }
      );

      const list: any[] = Array.isArray(rawResponse)
        ? rawResponse
        : Array.isArray(rawResponse?.transactions)
        ? rawResponse.transactions
        : Array.isArray(rawResponse?.data)
        ? rawResponse.data
        : [];

      const transactions: MtCenterTransaction[] = list.map((tx: any) => {
        const ref = String(tx.reference || '');
        const isTae =
          tx.kind === 'tae' ||
          tx.tae === true ||
          ref.startsWith('TAE-');

        return {
          id: String(tx.id || ''),
          userId: String(tx.userId || tx.user_id || ''),
          phoneNumber: String(tx.phoneNumber || tx.phone_number || ''),
          userName: String(tx.userName || tx.user_name || '').trim(),
          email: tx.email ?? null,
          kind: isTae ? 'tae' : 'service',
          amount: Number(tx.amount ?? 0),
          billAmount: Number(tx.billAmount ?? tx.bill_amount ?? tx.amount ?? 0),
          surcharge: Number(tx.surcharge ?? tx.feeAmount ?? tx.fee_amount ?? 0),
          feeAmount: Number(tx.feeAmount ?? tx.fee_amount ?? tx.surcharge ?? 0),
          providerId: tx.providerId ?? tx.provider_id ?? null,
          providerName: tx.providerName ?? tx.provider_name ?? null,
          accountNumber: tx.accountNumber ?? tx.account_number ?? null,
          rechargePhone: tx.rechargePhone ?? tx.recharge_phone ?? null,
          reference: ref,
          status: String(tx.status || 'unknown'),
          description: tx.description ?? null,
          mtCenterTicket: tx.mtCenterTicket ?? tx.mt_center_ticket ?? null,
          mtCenterTransaction: tx.mtCenterTransaction ?? tx.mt_center_transaction ?? null,
          createdAt: String(tx.createdAt || tx.created_at || ''),
        };
      });

      const summary = rawResponse?.summary || {};
      const totalAmount =
        typeof summary.totalAmount === 'number'
          ? summary.totalAmount
          : transactions.reduce((sum, t) => sum + t.amount, 0);
      const totalSurcharge =
        typeof summary.totalSurcharge === 'number'
          ? summary.totalSurcharge
          : transactions.reduce((sum, t) => sum + t.surcharge, 0);

      return {
        transactions,
        total: Number(rawResponse?.total ?? summary.count ?? transactions.length),
        limit: Number(rawResponse?.limit ?? filters?.limit ?? 50),
        offset: Number(rawResponse?.offset ?? filters?.offset ?? 0),
        summary: {
          count: Number(summary.count ?? rawResponse?.total ?? transactions.length),
          totalAmount: Math.round(totalAmount * 100) / 100,
          totalSurcharge: Math.round(totalSurcharge * 100) / 100,
        },
      };
    },
    [makeRequest]
  );

  // Customers
  const getCustomerByPhone = useCallback(async (phone: string): Promise<Customer> => {
    console.log('🔍 Searching customer by phone:', phone);
    const queryParam = `?phone=${encodeURIComponent(phone)}`;
    return makeRequest<Customer>(`/admin/customers${queryParam}`, {
      method: 'GET',
    });
  }, [makeRequest]);

  const getCustomer = useCallback(async (customerId: string): Promise<Customer> => {
    console.log('👤 Getting customer:', customerId);
    return makeRequest<Customer>(`/admin/customers/${customerId}`, {
      method: 'GET',
    });
  }, [makeRequest]);

  const updateCustomer = useCallback(async (customerId: string, data: UpdateCustomerRequest): Promise<Customer> => {
    console.log('✏️ Updating customer:', customerId, data);
    return makeRequest<Customer>(`/admin/customers/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  const sendOtp = useCallback(async (customerId: string, data?: SendOtpRequest): Promise<SendOtpResponse> => {
    console.log('📧 Sending OTP to customer:', customerId);
    return makeRequest<SendOtpResponse>(`/admin/customers/${customerId}/send-otp`, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }, [makeRequest]);

  const verifyOtp = useCallback(async (customerId: string, data: VerifyOtpRequest): Promise<VerifyOtpResponse> => {
    // NOTA: Este endpoint debe validar el PIN del cliente directamente
    // El campo "otp" contiene el PIN del usuario que debe compararse con el PIN almacenado en la BD
    console.log('✅ Verifying PIN/OTP for customer:', customerId);
    console.log('📝 Request body:', { ...data, otp: '***' }); // No loggear el PIN completo por seguridad
    return makeRequest<VerifyOtpResponse>(`/admin/customers/${customerId}/verify-otp`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  // CLABE endpoints
  const getUserClabe = useCallback(async (userIdOrPhone: string, usePhone?: boolean): Promise<{ clabe: string | null }> => {
    if (!userIdOrPhone) {
      throw new ApiError('User ID or phone is required to get CLABE', 400, 'VALIDATION_ERROR');
    }
    
    let endpoint: string;
    if (usePhone) {
      // Si se especifica usePhone, usar query parameter con teléfono
      endpoint = `/admin/users/clabe?phone=${encodeURIComponent(userIdOrPhone)}`;
      console.log('🔍 Getting user CLABE by phone:', userIdOrPhone);
    } else {
      // Por defecto, usar userId en la ruta
      endpoint = `/admin/users/${userIdOrPhone}/clabe`;
      console.log('🔍 Getting user CLABE for user ID:', userIdOrPhone);
    }
    
    return makeRequest<{ clabe: string | null }>(endpoint, {
      method: 'GET',
    });
  }, [makeRequest]);

  const updateUserClabe = useCallback(async (userId: string, clabe: string): Promise<Customer> => {
    // Validar que la CLABE tenga 18 dígitos
    if (clabe.length !== 18) {
      throw new ApiError('La CLABE debe tener exactamente 18 dígitos', 400, 'VALIDATION_ERROR');
    }
    
    console.log('💾 Updating user CLABE:', userId);
    return makeRequest<Customer>(`/admin/users/${userId}/clabe`, {
      method: 'POST',
      body: JSON.stringify({ clabe }),
    });
  }, [makeRequest]);

  // Customer Info and Balance
  const getCustomerInfo = useCallback(async (customerId: string): Promise<CustomerInfo> => {
    console.log('📋 Getting customer info:', customerId);
    return makeRequest<CustomerInfo>(`/admin/customers/${customerId}/info`, {
      method: 'GET',
    });
  }, [makeRequest]);

  const getCustomerBalance = useCallback(async (customerId: string): Promise<CustomerBalance> => {
    console.log('💰 Getting customer balance:', customerId);
    return makeRequest<CustomerBalance>(`/admin/customers/${customerId}/balance`, {
      method: 'GET',
    });
  }, [makeRequest]);

  const getCustomerInvestments = useCallback(async (
    customerId: string,
    startDate?: string,
    endDate?: string,
    status?: string
  ): Promise<CustomerInvestment[]> => {
    console.log('📈 Getting customer investments:', customerId);
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (status) params.append('status', status);
    const queryString = params.toString();
    const endpoint = `/admin/customers/${customerId}/investments${queryString ? `?${queryString}` : ''}`;
    return makeRequest<CustomerInvestment[]>(endpoint, {
      method: 'GET',
    });
  }, [makeRequest]);

  const getCustomerPurchases = useCallback(async (
    customerId: string,
    startDate?: string,
    endDate?: string,
    type?: string
  ): Promise<CustomerPurchase[]> => {
    console.log('🛒 Getting customer purchases:', customerId);
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (type) params.append('type', type);
    const queryString = params.toString();
    const endpoint = `/admin/customers/${customerId}/purchases${queryString ? `?${queryString}` : ''}`;
    return makeRequest<CustomerPurchase[]>(endpoint, {
      method: 'GET',
    });
  }, [makeRequest]);

  // Insurance Requests
  const getInsuranceRequests = useCallback(async (
    status?: string,
    search?: string,
    page?: number,
    limit?: number
  ): Promise<InsuranceRequestsResponse | InsuranceRequest[]> => {
    console.log('🛡️ Getting insurance requests');
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    const endpoint = `/admin/insurance-requests${queryString ? `?${queryString}` : ''}`;
    const response = await makeRequest<any>(endpoint, {
      method: 'GET',
    });
    
    // Handle both array and paginated response
    const rawData = Array.isArray(response) ? response : (response as any).data || [];
    
    // Log raw data to see what backend is sending
    if (rawData.length > 0) {
      console.log('🛡️ Raw insurance request data (first item):', {
        ...rawData[0],
        montoRaw: rawData[0].monto,
        montoType: typeof rawData[0].monto,
        coverage_amount: rawData[0].coverage_amount,
        monthly_premium: rawData[0].monthly_premium,
        annual_premium: rawData[0].annual_premium,
        allKeys: Object.keys(rawData[0])
      });
    }
    
    // Map response to match the new format
    const mappedData = rawData.map((item: any) => {
      // Determine monto from multiple possible fields
      // According to the format: coverage_amount || monthly_premium || annual_premium
      const monto = item.monto !== undefined && item.monto !== null 
        ? item.monto 
        : item.coverage_amount !== undefined && item.coverage_amount !== null
        ? item.coverage_amount
        : item.monthly_premium !== undefined && item.monthly_premium !== null
        ? item.monthly_premium
        : item.annual_premium !== undefined && item.annual_premium !== null
        ? item.annual_premium
        : null;
      
      return {
        ...item,
        // Use nombreCompleto from backend (new format)
        nombreCompleto: item.nombreCompleto || (item.nombre && item.apellidoPaterno 
          ? `${item.nombre} ${item.apellidoPaterno} ${item.apellidoMaterno || ''}`.trim()
          : null),
        // Beneficiario is now a simple string (can be "N/A" or the name)
        beneficiario: item.beneficiario || item.beneficiarioNombre || null,
        beneficiarioTelefono: item.beneficiarioTelefono || null,
        beneficiarioEmail: item.beneficiarioEmail || null,
        // Ensure optional fields can be null (use nullish coalescing to preserve 0 values)
        tipoSeguro: item.tipoSeguro ?? null,
        plazo: item.plazo ?? null,
        monto: monto, // Use the determined monto value
        rfc: item.rfc ?? null,
        curp: item.curp ?? null,
        sexo: item.sexo ?? null,
      };
    });
    
    // Return in the same format as received
    if (Array.isArray(response)) {
      return mappedData;
    } else {
      return {
        ...response,
        data: mappedData
      };
    }
  }, [makeRequest]);

  const getInsuranceRequest = useCallback(async (requestId: string): Promise<InsuranceRequest> => {
    console.log('🛡️ Getting insurance request:', requestId);
    return makeRequest<InsuranceRequest>(`/admin/insurance-requests/${requestId}`, {
      method: 'GET',
    });
  }, [makeRequest]);

  const updateInsuranceStatus = useCallback(async (
    requestId: string,
    data: UpdateInsuranceStatusRequest
  ): Promise<InsuranceRequest> => {
    console.log('✏️ Updating insurance request status:', requestId, data);
    return makeRequest<InsuranceRequest>(`/admin/insurance-requests/${requestId}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  // Investment Rates
  const getInvestmentRates = useCallback(async (): Promise<InvestmentRate[]> => {
    console.log('📊 Getting investment rates');
    return makeRequest<InvestmentRate[]>('/admin/investment-rates', {
      method: 'GET',
    });
  }, [makeRequest]);

  const updateInvestmentRate = useCallback(async (
    data: UpdateInvestmentRateRequest
  ): Promise<InvestmentRate> => {
    console.log('✏️ Updating investment rate:', data);
    return makeRequest<InvestmentRate>('/admin/investment-rates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  return {
    // Authentication (from context)
    isAuthenticated,
    accessToken,

    // Brands
    createBrand,
    getBrands,
    updateBrand,
    deleteBrand,

    // Branches
    createBranch,
    getBranches,
    updateBranch,
    deleteBranch,

    // POS Users
    createPosUser,
    getPosUsers,
    updatePosUser,
    deletePosUser,

    // Admin Users
    createAdminUser,
    getAdminUsers,
    updateAdminUser,
    deleteAdminUser,

    // External Transfers
    getExternalTransfers,
    updateExternalTransferStatus,
    completeExternalTransfer,

    // MT Center
    getMtCenterTransactions,

    // Customers
    getCustomerByPhone,
    getCustomer,
    updateCustomer,
    sendOtp,
    verifyOtp,

    // CLABE endpoints
    getUserClabe,
    updateUserClabe,

    // Customer Info and Balance
    getCustomerInfo,
    getCustomerBalance,
    getCustomerInvestments,
    getCustomerPurchases,

    // Insurance Requests
    getInsuranceRequests,
    getInsuranceRequest,
    updateInsuranceStatus,

    // Investment Rates
    getInvestmentRates,
    updateInvestmentRate,
  };
};
